import { NumberGrid } from '@Animation/mechanics/base/number-grid'
import { NumberSelector } from '@Animation/mechanics/base/number-selector'
import { KenoSnakesUtils } from './utils'
import { KenoSnake } from './keno-snake'
import { Grid } from '@Animation/game-objects/grid'

export class KenoSnakesNumberSelector extends NumberSelector {
  snake1: KenoSnake
  snake2: KenoSnake
  grid: Grid

  constructor(
    scene: Phaser.Scene,
    numberGrid: NumberGrid,
    miniGameSelections: number[],
    preNumberSelected: (index: number) => Promise<void>,
    onNumberSelected: (number: number) => void,
  ) {
    super(scene, numberGrid, miniGameSelections, preNumberSelected, onNumberSelected)

    this.snake1 = new KenoSnake(this.scene, this.numberGrid.x, this.numberGrid.y)
    this.snake1.setVisible(false)

    this.snake2 = new KenoSnake(this.scene, this.numberGrid.x, this.numberGrid.y)
    this.snake2.setVisible(false)

    this.grid = new Grid(8, 10)

    this.add([this.snake1, this.snake2])
  }

  async skip(numbers: number[]) {
    super.skip(numbers)
    this.snake1.setVisible(false)
    this.snake2.setVisible(false)
  }

  async targetSingleNumber(number: number): Promise<void> {
    let snakeMoveDuration = 80

    const endPosition = this.grid.mapIndexToGridLocation(number - 1)
    const adjPositions = this.grid.adjacentGridLocation(endPosition)

    const snake1Start = this.grid.getRandomGridLocation(undefined, 0)
    const snake2Start = this.grid.getRandomGridLocation(undefined, 9)

    let randomIndex = Math.floor(Math.random() * adjPositions.length)
    const s1Target = adjPositions.splice(randomIndex, 1)[0]
    randomIndex = Math.floor(Math.random() * adjPositions.length)
    const s2Target = adjPositions.splice(randomIndex, 1)[0]

    const snake1Path = KenoSnakesUtils.createPath(this.grid, snake1Start, endPosition, s1Target, 12)
    const snake2Path = KenoSnakesUtils.createPath(this.grid, snake2Start, endPosition, s2Target, 12)

    const numSteps = Math.max(snake1Path.length, snake2Path.length)
    const snake1Delay = numSteps - snake1Path.length
    const snake2Delay = numSteps - snake2Path.length

    const s1StartNumber = this.grid.mapGridLocationToIndex(snake1Path[0].location)
    this.snake1.setSnakePosition(this.numberGrid.x - 96, this.numberGrid.numbers[s1StartNumber].y)

    const s2StartNumber = this.grid.mapGridLocationToIndex(snake2Path[0].location)
    this.snake2.setSnakePosition(this.numberGrid.x + 576, this.numberGrid.numbers[s2StartNumber].y)

    this.snake1.startMovement()
    this.snake2.startMovement()

    for (let i = 0; i < numSteps; i++) {
      snakeMoveDuration += 3
      const movePromises = []

      const s1Index = i - snake1Delay
      const s2Index = i - snake2Delay

      if (s1Index >= 0) {
        const target =
          this.numberGrid.numbers[
            this.grid.mapGridLocationToIndex(snake1Path[i - snake1Delay].location)
          ]
        movePromises.push(this.snake1.moveSnake(target.x, target.y, snakeMoveDuration))
      }

      if (s2Index >= 0) {
        const target =
          this.numberGrid.numbers[
            this.grid.mapGridLocationToIndex(snake2Path[i - snake2Delay].location)
          ]
        movePromises.push(this.snake2.moveSnake(target.x, target.y, snakeMoveDuration))
      }

      await Promise.all(movePromises)
    }

    const stop1 = this.snake1.stopMovement()
    const stop2 = this.snake2.stopMovement()

    await Promise.all([stop1, stop2])

    const selectNumber = this.numberGrid.selectNumber(number)
    await Promise.all([selectNumber])
  }
}
